import { useState, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';
import upload from '../../../Assets/images/bootstrap-upload.png';
import { responsiveSizeTimesheet, validationMessages } from '../../../utils/constants';
import classes from './CommonFileUploader.module.scss';
import { useWindowSize } from '../../../utils/hooks';
import AddAttachmentIcon from '../../common/Icons/AddAttachmentIcon';
import NotificationModal from '../../common/ModalWindows/NotificationModal';

export default function CommonFileUploader({
  uploadHandler,
  acceptedFormats = ['.pdf', '.docx', '.doc'],
  maxFileSizeMB = 10,
  isMultiple = true,
  allowedQtyOfAttachments = 10,
  existingAttachmentsQty = 9,
  disabled = false,
  loading
}) {
  const [error, setError] = useState('');
  const size = useWindowSize();
  const [t] = useTranslation();
  const validationMessage = useMemo(() => validationMessages(t), [t]);

  const megabyte = 1024 * 1024;

  const validateFiles = (filesList) => {
    if (!isMultiple && filesList.length > 1) {
      setError(validationMessage.CV_ONLY_ONE_FILE);
      return false;
    }

    if (
      Object.values(filesList).some((file) => {
        return file.size > maxFileSizeMB * megabyte;
      })
    ) {
      setError(
        `${t('profile.validations.FILE_SIZE_LIMIT1')}${maxFileSizeMB}${t('profile.validations.FILE_SIZE_LIMIT2')}`
      );
      t();
      return false;
    }

    if (
      Object.values(filesList).some((file) => {
        const tempArray = file.name.split('.');
        return !acceptedFormats.includes(`.${tempArray[tempArray.length - 1]}`);
      })
    ) {
      setError(
        `${t('profile.validations.FILE_EXTENSION_LIMIT1')}${acceptedFormats.join(', ').toUpperCase()} ${t(
          'profile.validations.FILE_EXTENSION_LIMIT2'
        )}`
      );
      return false;
    }

    if (existingAttachmentsQty === allowedQtyOfAttachments) {
      setError(
        `${t('profile.validations.FILES_QTY_LIMIT1')}${allowedQtyOfAttachments} ${t(
          'profile.validations.FILES_QTY_LIMIT2'
        )}`
      );
      return false;
    }

    setError('');
    return true;
  };

  const onDropHandler = (ev) => {
    ev.stopPropagation();
    ev.preventDefault();

    const isValid = validateFiles(ev.dataTransfer.files);

    if (isValid) uploadHandler(ev.dataTransfer.files);
  };

  const onChangeHandler = (ev) => {
    const isValid = validateFiles(ev.target.files);

    if (isValid) uploadHandler(ev.target.files);
  };

  const onDragOver = (ev) => ev.preventDefault();

  return (
    <div className={classes.container}>
      <form>
        <button className={classes.dropZone} onDrop={onDropHandler} onDragOver={onDragOver} type="button">
          {!loading ? (
            <label htmlFor="fileUploader" className={classes.labelContainer}>
              {size.width > responsiveSizeTimesheet ? (
                <>
                  <img src={upload} alt="file upload" className={classes.uploadImage} />
                  <div>
                    <label htmlFor="fileUploader" className={classes.phrase1}>
                      {t('profile.builder.start.drop')}&nbsp;
                    </label>
                    <label htmlFor="fileUploader" className={classes.phrase2}>
                      <Trans i18nKey="profile.builder.start.browse">
                        or<span> browse</span>
                      </Trans>
                    </label>
                  </div>
                </>
              ) : (
                <div className={classes.mobileLabel}>
                  <div className={classes.composableSvgContainer}>
                    <AddAttachmentIcon />
                  </div>
                  <p>{t('profile.builder.start.attachment')}</p>
                  <div />
                </div>
              )}

              <input
                disabled={disabled}
                id="fileUploader"
                multiple={isMultiple}
                type="file"
                accept={acceptedFormats.join(',')}
                onChange={onChangeHandler}
                onClick={(event) => {
                  event.currentTarget.value = null;
                }}
                style={{ display: 'none' }}
              />
            </label>
          ) : (
            <div className={classes.loadingContainer}>
              <CircularProgress size={20} />
              {t('common.general.uploading')}...
            </div>
          )}
        </button>
      </form>
      {error && (
        <NotificationModal
          open={error}
          onClose={() => setError('')}
          paddingX={size.width > responsiveSizeTimesheet ? '36px' : '49px'}
          height={size.width > responsiveSizeTimesheet ? '450px' : '600px'}
        >
          <p className={classes.errorMessage}>{error}</p>
        </NotificationModal>
      )}
    </div>
  );
}
